<template>
  <div>
    <div v-if="loading"><Loader /></div>
    <div v-else>
      <div class="publications__list">
        <div
          v-for="publication in publications"
          :key="publication.id"
          class="publication"
        >
          <div class="publication__section__thumb">
            <img class="publication__thumb" :src="publication.thumbnail" />
          </div>
          <div class="publication__info">
            <p class="publication__authors">
              Por | {{ getAuthors(publication) }}
            </p>
            <a
              :href="`http://hdl.handle.net/${publication.handle}`"
              target="_blank"
              class="publication__title"
            >
              {{ getMetadataValue(publication, 64) }}</a
            >
            <p class="publication__abstract">
              {{ getMetadataValue(publication, 27) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Paginación -->
      <div class="pagination">
        <button
          class="btn__pagination"
          @click="prevPage"
          :disabled="currentPage === 1"
        >
          Anterior
        </button>
        <span>Página {{ currentPage }} de {{ totalPages }}</span>
        <button
          class="btn__pagination"
          @click="nextPage"
          :disabled="currentPage === totalPages"
        >
          Siguiente
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import { getPublicationsByTwoConditions } from "@/api/publicationsLamar.js";
import Loader from "@/components/common/Loader";

export default {
  name: "Publications",
  components: {
    Loader,
  },
  setup() {
    const publications = ref([]);
    const loading = ref(true);
    const currentPage = ref(1);
    const totalPages = ref(0);
    const paginatorInfo = ref({ hasMorePages: false });
    const totalItems = ref(0);

    // Función auxiliar para obtener metadatos
    const getMetadataValue = (publication, metadataFieldId) =>
      publication.metadata.find(
        (meta) => meta.metadata_field_id === metadataFieldId
      )?.value || "No disponible";

    const getAuthors = (publication) => {
      return getMetadataArray(publication, 3);
    };

    const getMetadataArray = (publication, metadataFieldId) => {
      const search = publication.metadata.filter(
        (meta) => meta.metadata_field_id === metadataFieldId
      );
      // Format APA
      if (metadataFieldId === 3) {
        return search.map((meta) => getAPAformat(meta.value)).join("; ");
      }
    };

    const getAPAformat = (value) => {
      const fullName = value.split(",");
      const surname = fullName[0];
      let authorName = surname;
      if (fullName.length === 2) {
        authorName += `, ${fullName[1].substring(0, 2)}.`;
      }
      return authorName;
    };

    // Función para obtener publicaciones
    const fetchPublications = async (page) => {
      loading.value = true;
      try {
        const result = await getPublicationsByTwoConditions({
          url: "https://repositorio.igp.gob.pe/REGEN_api/public/graphql",
          currentPage: page,
          itemsPerPage: 10,
        });
        publications.value = result.data;
        paginatorInfo.value = result.paginatorInfo;
        totalItems.value = result.paginatorInfo.total;
        totalPages.value = Math.ceil(result.paginatorInfo.total / 10); // Calcula el total de páginas
      } catch (error) {
        console.error("Error al obtener publicaciones:", error);
      } finally {
        loading.value = false;
      }
    };

    // Función para ir a la página anterior
    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--;
      }
    };

    // Función para ir a la página siguiente
    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++;
      }
    };

    // Monitoreo de cambios en la página actual
    watch(currentPage, (newPage) => {
      fetchPublications(newPage);
    });

    // Inicializar publicaciones al montar el componente
    onMounted(() => {
      fetchPublications(currentPage.value);
    });

    return {
      publications,
      loading,
      currentPage,
      totalPages,
      paginatorInfo,
      getMetadataValue,
      getMetadataArray,
      getAPAformat,
      getAuthors,
      prevPage,
      nextPage,
    };
  },
};
</script>

<style lang="scss" scoped>
@use "@/assets/styles/settings/_variables.scss";
.publications__list {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.publication {
  display: flex;
  gap: 2rem;
  line-height: 1.5;
}

.publication__section__thumb {
  flex: none;
}

.publication__authors {
  font-size: 0.9rem;
  margin-bottom: 0px;
}

.publication__title {
  padding: 0.4rem 0;
}

.publication__thumb {
  /* no flex */
  width: 120px;
  border-radius: 5px;
  border: 1px solid #ccc;
}

.publication__info {
  display: flex;
  flex-direction: column;
}

.publication__abstract {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0;
  font-size: 0.9rem;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: end;
  align-items: center;
  font-size: 0.98rem;
  gap: 10px;
}

.btn__pagination {
  border: none;
  font-size: 0.98rem;
  padding: 0.8rem 1rem;
  border-radius: 5px;
  cursor: pointer;
  background-color: variables.$primary-color;
  color: variables.$background-lighter-color;
  &:hover {
    background-color: variables.$primary-dark-color;
  }
  &:disabled {
    background-color: #f5f5f5;
    color: #b4b4b4;
    cursor: not-allowed;
  }
}

button {
  margin: 0 10px;
}
</style>
